import { ArrowLeftOutlined, AudioOutlined, PauseOutlined, RedoOutlined } from '@ant-design/icons';
import { Alert, Button, Col, Flex, Row } from 'antd';
import RecordingRecorder from 'components/Message/CreateMessageComponent/StepMessage/RecordingRecorder/RecordingRecorder';
import { useReactMediaRecorder } from 'components/Tool/ReactMediaRecorder/ReactMediaRecorder';
import SpinComponent from 'components/Tool/SpinComponent/SpinComponent';
import { getMessageMediaConstraint } from 'constants/mediaConstraints';
import { TextToHtmlComponent } from 'components/UiKit/TextToHtmlComponent/TextToHtmlComponent';
import { useEffect, useRef, useState } from 'react';
import { sessionActivity } from 'utils/sessionActivity';
import './AudioRecorder.css';

const MAX_RECORD_TIME = getMessageMediaConstraint('audioMessage').maxDurationSeconds || 120;

type AllProps = {
  scriptText?: string;
  onDone: (file: File) => void;
  onCancel: () => void;
};

function AudioRecorder({ scriptText, onDone, retake, onCancel }: AllProps & { retake: () => void }) {
  const refStop = useRef({ isStopped: false });
  const clearBlobUrlRef = useRef<() => void>(() => undefined);

  const { status, error, startRecording, stopRecording, pauseRecording, resumeRecording, clearBlobUrl } =
    useReactMediaRecorder({
      video: false,
      audio: true,
      askPermissionOnMount: true,
      mediaRecorderOptions: { mimeType: 'audio/wav' },
      onStop: (blobUrl: string, blob: Blob) => {
        if (refStop.current.isStopped) {
          onDone(new File([blob], `${new Date().getTime()}.wav`, { type: 'audio/wav' }));
        }
      }
    });

  const stopAudioRecording = () => {
    refStop.current.isStopped = true;
    stopRecording();
  };

  const isRecordingInProgress = status === 'recording' || status === 'paused';

  useEffect(() => {
    clearBlobUrlRef.current = clearBlobUrl;
  }, [clearBlobUrl]);

  useEffect(() => {
    return () => {
      clearBlobUrlRef.current();
    };
  }, []);

  useEffect(() => {
    sessionActivity.setRecordingActive(isRecordingInProgress);

    return () => {
      sessionActivity.setRecordingActive(false);
    };
  }, [isRecordingInProgress]);

  const handleRetake = () => {
    clearBlobUrl();
    retake();
  };

  return (
    <div className="audio-recorder create-message-component-full-height">
      <SpinComponent isLoading={status === 'acquiring_media'}>
        {error ? (
          <Alert className="error-message" message={error} type="error" showIcon />
        ) : (
          <div className="recorder-container">
            <div>
              <div className="audio-overlay">
                <RecordingRecorder status={status} maxTime={MAX_RECORD_TIME} onTimeEnds={stopAudioRecording} />
                <AudioOutlined className="microphone" />
              </div>

              <Flex gap={30} className="audio-buttons" justify="center">
                <Button size="large" onClick={handleRetake}>
                  <RedoOutlined /> Retake
                </Button>

                {status === 'recording' ? (
                  <Button size="large" type="primary" onClick={pauseRecording}>
                    <PauseOutlined /> Pause / Stop
                  </Button>
                ) : (
                  <Button
                    size="large"
                    type="primary"
                    onClick={() => (status === 'idle' ? startRecording() : resumeRecording())}
                  >
                    <AudioOutlined /> {status === 'idle' ? 'Start Recording' : 'Continue Recording'}
                  </Button>
                )}
              </Flex>
            </div>

            {scriptText && <TextToHtmlComponent text={scriptText} />}
          </div>
        )}
      </SpinComponent>

      <Row className="create-message-component-footer">
        <Col span={12}>
          <Button onClick={onCancel} type="link">
            <ArrowLeftOutlined /> Back
          </Button>
        </Col>

        <Col span={12} className="text-right">
          <Button
            type="primary"
            htmlType="submit"
            disabled={status !== 'paused' && status !== 'recording'}
            onClick={stopAudioRecording}
          >
            Next
          </Button>
        </Col>
      </Row>
    </div>
  );
}

function AudioRecorderWrapper(props: AllProps) {
  const [retakeCount, setRetakeCount] = useState(1);

  return <AudioRecorder key={retakeCount} {...props} retake={() => setRetakeCount((count) => count + 1)} />;
}

export default AudioRecorderWrapper;
